import { Injectable, OnDestroy } from '@angular/core'
import { Observable, BehaviorSubject, of, Subscription, from } from 'rxjs'
import { map, catchError, switchMap, finalize } from 'rxjs/operators'
import { UserModel } from '../_models/user.model'
import { AuthModel } from '../_models/auth.model'
import { ApiService } from 'src/app/_metronic/shared/http/api.service'
import { MsalService } from '@azure/msal-angular'
import {
  HEADER_ACCEPT,
  authLocalStorageToken,
  LOGIN_PATH,
  LOGIN_PATH_SELECT_ACCOUNT,
  odooInfo,
  workLocalInfo
} from 'src/app/_metronic/shared/shared/common/helper'
import { RequestPayload } from 'src/app/_metronic/shared/http/request-payload.model'

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = [] // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  // public fields
  currentUser$: Observable<UserModel>
  isLoading$: Observable<boolean>
  currentUserSubject: BehaviorSubject<UserModel>
  isLoadingSubject: BehaviorSubject<boolean>
  dataDetail = null
  query = null

  get currentUserValue(): UserModel {
    return this.currentUserSubject.value
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user)
  }

  constructor(private api: ApiService, private authMsalService: MsalService) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false)
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined)
    this.currentUser$ = this.currentUserSubject.asObservable()
    this.isLoading$ = this.isLoadingSubject.asObservable()
    const subscr = this.getUserByToken().subscribe()
    this.unsubscribe.push(subscr)
  }

  // public methods
  login(username: string, password: string): Observable<UserModel> {
    this.isLoadingSubject.next(true)
    return this.api.post('/api/Login', { username, password }).pipe(
      map((auth: AuthModel) => {
        const result = this.setAuthFromLocalStorage(auth)
        return result
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err)
        return of(undefined)
      }),
      finalize(() => this.isLoadingSubject.next(false))
    )
  }

  /**
   * @description Đăng xuất TOÀN CỤC, kết thúc luôn session SSO ở Azure AD.
   * @description Chỉ dùng cho nút Đăng xuất người dùng chủ động bấm: mọi ứng dụng khác
   * @description dùng chung tenant cũng bị đăng xuất theo.
   * @return void
   */
  logout() {
    const activeAccount = this.authMsalService.instance.getActiveAccount()
    this.api.logout(this.authMsalService, activeAccount)
  }

  /**
   * @description Đăng xuất CỤC BỘ rồi mở trang đăng nhập - dùng cho các luồng xử lý lỗi.
   * @description Giữ nguyên session SSO của Azure AD nên không ảnh hưởng ứng dụng khác.
   * @param redirectTo: string trang sẽ mở sau khi dọn phiên
   * @return void
   */
  logoutLocal(redirectTo: string = LOGIN_PATH) {
    const activeAccount = this.authMsalService.instance.getActiveAccount()
    this.api.logoutLocal(this.authMsalService, activeAccount, redirectTo)
  }

  getUserByToken(): Observable<UserModel> {
    const auth = this.getAuthFromLocalStorage()
    if (!auth || !auth.accessToken) {
      return of(undefined)
    }

    const body = {
      access_token: auth?.accessToken,
      employee_id: parseInt(auth?.employeeID)
    }
    this.dataDetail = {}
    this.isLoadingSubject.next(true)
    this.api.loadingCustomOn('Checking user')
    return this.api
      .post('/hrm_tcg/api/v1/tcm/employee/information', body, HEADER_ACCEPT)
      .pipe(
        //switchMap(m => from([m?.result?.data?.app_data].
        map((response: any) => {
          const { result } = response
          if (result?.code === 200) {
            const app_data = result?.data?.app_data ?? []
            Object.keys(app_data).forEach((k) => {
              this.dataDetail[k] = app_data[k]?.value ?? app_data[k]
            })
            const user = {
              ...app_data,
              hoTen: this.dataDetail?.name,
              emailCongTy: this.dataDetail?.work_email,
              tenChucVu: this.dataDetail?.job?.name,
              diaChiCuTru: this.dataDetail?.current_address,
              soDienThoai: this.dataDetail?.mobile_phone,
              tenPhongBan: this.dataDetail?.department?.name,
              soMayLe: this.dataDetail?.work_phone,
              anhDaiDien: this.dataDetail?.img_url,
              id: this.dataDetail?.id,
              maBan: this.dataDetail?.department_parent?.code,
              tenBan: this.dataDetail?.department_parent?.name,
              diaChiMacDinh: this.dataDetail?.default_address,
              maNhanVien: this.dataDetail?.code,
              ham: null,
              chucDanh: null,
              maSoThue: this.dataDetail?.tax_code,
              soBHXH: this.dataDetail?.social_insurance_code,
              viTri: this.dataDetail?.position,
              bangCap: this.dataDetail?.training,
              lichSuCongTacTaiCongTy: this.dataDetail?.transfer,
              lichSuCongTacNgoaiCongTy: this.dataDetail?.transfer_outer,
              ngaySinh: this.dataDetail?.birthday
            }
            if (user) {
              this.currentUserSubject.next(user)
              localStorage.setItem(workLocalInfo, JSON.stringify({ workEmail: user.emailCongTy, departmentCode: user.maBan }))
            }
            return user
          } else {
            /**
             * Luồng lỗi: token AAD vẫn hợp lệ, chỉ là portal không lấy được hồ sơ nhân
             * viên. Đăng xuất cục bộ để không kéo theo các ứng dụng khác cùng tenant.
             */
            console.error("Không lấy được thông tin nhân viên")
            this.logoutLocal(LOGIN_PATH_SELECT_ACCOUNT)
          }
        }),
        catchError((err) => {
          console.error('[Auth] Không lấy được thông tin nhân viên.', err)
          this.currentUserSubject.next(undefined)
          return of(undefined)
        }),
        finalize(() => {
          this.isLoadingSubject.next(false)
          this.api.loadingCustomOff()
        }),
      )
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true)
    return this.api.post('', user).pipe(
      map(() => {
        this.isLoadingSubject.next(false)
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err)
        return of(undefined)
      }),
      finalize(() => this.isLoadingSubject.next(false))
    )
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true)
    return this.api
      .post('/api/forgot-password', { email })
      .pipe(finalize(() => this.isLoadingSubject.next(false)))
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth accessToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.accessToken) {
      localStorage.setItem(authLocalStorageToken, JSON.stringify(auth))
      return true
    }
    return false
  }

  public getAuthFromLocalStorage(): any {
    try {
      const authData = JSON.parse(localStorage.getItem(authLocalStorageToken))
      return authData
    } catch (error) {
      console.error(error)
      return undefined
    }
  }

  public getOdooInfoFromLocalStorage(): any {
    try {
      const odooData = JSON.parse(localStorage.getItem(odooInfo))
      return odooData
    } catch (error) {
      console.error(error)
      return undefined
    }
  }

  currentUserBiReport(filter?: RequestPayload) {
    return this.api.getOffLoading('/bi_tcg/account/current-user', this.query, {
      params: filter?.toParams()
    })
  }

  documentUserPermission() {
    return this.api.getOffLoading('/backend_tcg/base/users/permissions', this.query, { headers: { 'Token': 'Azure' } })
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe())
  }

  /**
   * login odoo for eoffice system
   * @param param0 param login odoo
   */
  public loginOdooSystem({ type = 'microsoft', accessToken, device_id = 'deviceId', firebaseToken = 'fcmToken' }) {
    const body = {
      type: type,
      accessToken: accessToken,
      device_id: device_id,
      firebase_token: firebaseToken
    }
    return this.api.postOffLoading('/hrm_tcg/api/v1/tcm/sign_in', body, HEADER_ACCEPT);
  }
}
